import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { PlusIcon, TrashIcon, MagnifyingGlassIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { PaginatedResponse, Dataset } from '../types'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'

export default function DatasetsPage() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['datasets'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Dataset>>('/datasets', {
        params: { page_size: 100 },
      })
      return res.data
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => api.delete(`/datasets/${id}`)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] })
      setSelectedIds(new Set())
    },
  })

  const bulkDownloadMutation = useMutation({
    mutationFn: async (ids?: string[]) => {
      const params = new URLSearchParams()
      ids?.forEach((id) => params.append('dataset_ids', id))

      // Fetch the list of upload IDs/filenames (no presigned URLs, just metadata)
      const res = await api.get<{ upload_id: string; filename: string; file_size_bytes?: number }[]>(
        '/uploads/download/urls',
        { params },
      )

      if (!res.data.length) throw new Error('No files')

      // Download each file sequentially through the backend proxy so
      // MinIO does NOT need to be publicly accessible.
      for (const item of res.data) {
        const fileRes = await api.get(`/uploads/${item.upload_id}/download`, {
          responseType: 'blob',
        })
        const url = window.URL.createObjectURL(fileRes.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', item.filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        // Small gap so browser registers each download separately
        await new Promise((r) => setTimeout(r, 200))
      }
    },
    onError: () => {
      toast.error('No uploaded CSV files found to download')
    },
  })

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const filteredItems = useMemo(() => {
    if (!data?.items) return []
    if (!searchQuery.trim()) return data.items
    const query = searchQuery.toLowerCase()
    return data.items.filter(d => 
      d.name.toLowerCase().includes(query) ||
      d.description?.toLowerCase().includes(query)
    )
  }, [data?.items, searchQuery])

  const toggleSelectAll = () => {
    if (!filteredItems.length) return
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredItems.map(d => d.id)))
    }
  }

  const handleDelete = () => {
    if (selectedIds.size === 0) return
    if (confirm(`Delete ${selectedIds.size} dataset(s)? This cannot be undone.`)) {
      deleteMutation.mutate(Array.from(selectedIds))
    }
  }

  const handleBulkDownload = () => {
    const selected = Array.from(selectedIds)
    bulkDownloadMutation.mutate(selected.length ? selected : undefined)
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load datasets</p>
      </div>
    )
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Datasets</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your uploaded data
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleBulkDownload}
            disabled={bulkDownloadMutation.isPending}
            className="btn-secondary"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            {selectedIds.size > 0 ? `Download CSVs (${selectedIds.size})` : 'Download All CSVs'}
          </button>
          {selectedIds.size > 0 && (
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="btn-danger"
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              Delete ({selectedIds.size})
            </button>
          )}
          <Link to="/upload" className="btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Upload CSV
          </Link>
        </div>
      </div>

      {/* Search */}
      {data?.items.length ? (
        <div className="mb-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search datasets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full max-w-md"
            />
          </div>
        </div>
      ) : null}
      
      {filteredItems.length ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={filteredItems.length > 0 && selectedIds.size === filteredItems.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rows
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Columns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((dataset) => (
                <tr key={dataset.id} className={`hover:bg-gray-50 ${selectedIds.has(dataset.id) ? 'bg-primary-50' : ''}`}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(dataset.id)}
                      onChange={() => toggleSelect(dataset.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Link
                      to={`/datasets/${dataset.id}`}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {dataset.name}
                    </Link>
                    {dataset.description && (
                      <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                        {dataset.description}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      dataset.status === 'ready' 
                        ? 'bg-green-100 text-green-700'
                        : dataset.status === 'processing'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {dataset.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {dataset.row_count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {dataset.column_count}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDistanceToNow(new Date(dataset.created_at), { addSuffix: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : data?.items.length ? (
        <div className="card p-12 text-center">
          <p className="text-gray-500 mb-4">No datasets match "{searchQuery}"</p>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-gray-500 mb-4">No datasets yet</p>
          <Link to="/upload" className="btn-primary">
            Upload your first CSV
          </Link>
        </div>
      )}
    </div>
  )
}
